import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadSettings();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    // CRITICAL: returning from OAuth — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signIn = useCallback(
    async (nextUser) => {
      setUser(nextUser);
      setLoading(false);
      await loadSettings();
    },
    [loadSettings],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      setSettings(null);
    }
  }, []);

  const currency = settings?.currency_symbol || "Rs.";

  return (
    <AuthContext.Provider
      value={{ user, settings, setSettings, loadSettings, loading, signIn, logout, currency, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}
